/**
 * MD-11 FE-CHAT — citation span render helper
 *
 * Splits the message buffer into segments around citation markers:
 *
 *   Input:
 *     text = "Theo Điều 38 LHN, quyền nuôi con..."
 *     markers = [{cid:'c1', span_start:9, span_end:17}]
 *
 *   Output:
 *     [
 *       { kind:'text', text:'Theo Điều' },
 *       { kind:'cite', cid:'c1', text:' 38 LHN ' },
 *       { kind:'text', text:', quyền nuôi con...' }
 *     ]
 *
 * Caller renders <mark data-cid> for cite segments.  Day-1 markers MUST be
 * non-overlapping + sorted ascending by span_start. Defensive: drops invalid
 * markers (out-of-range / overlap) silently with no exception.
 */

export type CitationMarker = {
  cid: string;
  span_start: number;
  span_end: number;
};

export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'cite'; cid: string; text: string };

export function splitByCitations(text: string, markers: CitationMarker[]): Segment[] {
  if (!text) return [];

  // Filter + sort markers; drop overlap / out-of-range
  const valid: CitationMarker[] = [];
  let lastEnd = 0;
  const sorted = [...markers].sort((a, b) => a.span_start - b.span_start);
  for (const m of sorted) {
    if (
      m.span_start < 0 ||
      m.span_end > text.length ||
      m.span_start >= m.span_end ||
      m.span_start < lastEnd
    ) {
      continue;
    }
    valid.push(m);
    lastEnd = m.span_end;
  }

  const segments: Segment[] = [];
  let cursor = 0;
  for (const m of valid) {
    if (m.span_start > cursor) {
      segments.push({ kind: 'text', text: text.slice(cursor, m.span_start) });
    }
    segments.push({ kind: 'cite', cid: m.cid, text: text.slice(m.span_start, m.span_end) });
    cursor = m.span_end;
  }
  if (cursor < text.length) {
    segments.push({ kind: 'text', text: text.slice(cursor) });
  }
  return segments;
}

/**
 * Render segments to plain string with `<mark data-cid="...">` spans
 * (test-friendly — production component uses React JSX text nodes).
 *
 * NEVER use this output as `dangerouslySetInnerHTML` — the input `text`
 * has not been HTML-escaped. Caller should escape segment text per
 * F-FE-CHAT §8 XSS prevention.
 */
export function renderSegmentsRaw(segments: Segment[]): string {
  return segments
    .map((s) =>
      s.kind === 'text' ? s.text : `<mark data-cid="${s.cid}">${s.text}</mark>`
    )
    .join('');
}
